import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AgencyLogo from '../components/AgencyLogo';
import type { Agency } from '../types';

const PAGE_SIZE = 50;

const AgencyListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', acronym: '', street: '', city: '', state: '', postal_code: '', website: '', contact_procedures: '' });
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.role === 'Administrator' || user?.role === 'Superuser';

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line

  const { data, isLoading } = useQuery({
    queryKey: ['agencies', search, offset, showArchived],
    queryFn: () => api.getAgencies({
      q: search || undefined,
      limit: PAGE_SIZE,
      offset,
      active_only: showArchived ? 'false' : 'true',
    }),
    placeholderData: (prev) => prev,
  });

  const agencies: Agency[] = data?.data ?? [];
  const total: number = data?.meta?.total ?? agencies.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const createMutation = useMutation({
    mutationFn: () => api.createAgency({
      name:               createForm.name.trim(),
      acronym:            createForm.acronym.trim(),
      street:             createForm.street.trim()  || null,
      city:               createForm.city.trim()    || null,
      state:              createForm.state.trim()   || null,
      postal_code:        createForm.postal_code.trim() || null,
      website:            createForm.website.trim() || null,
      contact_procedures: createForm.contact_procedures.trim() || null,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setShowCreate(false);
      setCreateForm({ name: '', acronym: '', street: '', city: '', state: '', postal_code: '', website: '', contact_procedures: '' });
      const newId = res?.data?.id;
      if (newId) navigate(`/agencies/${newId}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setSearch(q);
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <form onSubmit={handleSearch} style={{ flex: 1 }}>
          <div className="searchbar">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search agencies by name or acronym…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" style={{ marginLeft: 8 }}>
              Search
            </button>
          </div>
        </form>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ flexShrink: 0 }}>
            <Plus size={14} />
            New Agency
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        {total > 0 && (
          <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>
            {total.toLocaleString()} agenc{total !== 1 ? 'ies' : 'y'}
          </div>
        )}
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', marginLeft: 'auto' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setOffset(0); }}
            />
            Show archived agencies
          </label>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Acronym</th>
              <th>Agency Name</th>
              <th>Contacts</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>
                  Loading…
                </td>
              </tr>
            ) : agencies.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>
                  No agencies found
                </td>
              </tr>
            ) : (
              agencies.map((a) => (
                <tr
                  key={a.id}
                  style={{ cursor: 'pointer', opacity: a.is_active ? 1 : 0.6 }}
                  onClick={() => navigate(`/agencies/${a.id}`)}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AgencyLogo name={a.name} acronym={a.acronym ?? undefined} size={36} />
                      <span className="chip" style={{ fontWeight: 600 }}>{a.acronym}</span>
                    </div>
                  </td>
                  <td style={{ fontWeight: 500 }}>{a.name}</td>
                  <td style={{ color: 'var(--gray-600)' }}>
                    {a.contact_count != null ? a.contact_count.toLocaleString() : '—'}
                  </td>
                  <td>
                    <span
                      className="pill"
                      style={{
                        background: a.is_active ? 'var(--green-50)' : 'var(--gray-100)',
                        color: a.is_active ? 'var(--green)' : 'var(--gray-500)',
                      }}
                    >
                      {a.is_active ? 'Active' : 'Archived'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <span style={{ fontSize: 14 }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Create Agency Modal */}
      {showCreate && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div className="modal" style={{ maxWidth: 480 }} onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>New Agency</h3>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}><X size={16} /></button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Agency Name *</label>
              <input
                className="field-input"
                type="text"
                placeholder="e.g. Department of Sanitation"
                autoFocus
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Acronym *</label>
              <input
                className="field-input"
                type="text"
                placeholder="e.g. DSNY"
                value={createForm.acronym}
                onChange={(e) => setCreateForm({ ...createForm, acronym: e.target.value })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="field-label">City</label>
                <input
                  className="field-input"
                  type="text"
                  placeholder="e.g. New York"
                  value={createForm.city}
                  onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">State</label>
                <input
                  className="field-input"
                  type="text"
                  placeholder="e.g. NY"
                  value={createForm.state}
                  onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="field-label">Street</label>
                <input
                  className="field-input"
                  type="text"
                  placeholder="e.g. 100 Gold St"
                  value={createForm.street}
                  onChange={(e) => setCreateForm({ ...createForm, street: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">Postal Code</label>
                <input
                  className="field-input"
                  type="text"
                  placeholder="e.g. 10038"
                  value={createForm.postal_code}
                  onChange={(e) => setCreateForm({ ...createForm, postal_code: e.target.value })}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Website</label>
              <input
                className="field-input"
                type="url"
                placeholder="https://…"
                value={createForm.website}
                onChange={(e) => setCreateForm({ ...createForm, website: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Contact Procedures</label>
              <textarea
                className="field-input"
                rows={3}
                placeholder="How to contact this agency…"
                value={createForm.contact_procedures}
                onChange={(e) => setCreateForm({ ...createForm, contact_procedures: e.target.value })}
              />
            </div>

            {createMutation.isError && (
              <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 10px' }}>
                {(createMutation.error as any)?.response?.data?.error ?? 'Failed to create agency.'}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!createForm.name.trim() || !createForm.acronym.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Creating…' : 'Create Agency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyListPage;
